import type { FunctionComponent } from 'react';
import { useCallback, useMemo } from 'react';

import { useFeatureGate } from '@trello/feature-gate-client';
import { useBoardId, useCardId } from '@trello/id-context';
import { useSharedStateSelector } from '@trello/shared-state';
import type { CardFrontTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { useIsInboxBoard } from 'app/src/components/Inbox';
import { notificationsState } from 'app/src/components/NotificationsMenu';
import { ArchivedBadge } from './ArchivedBadge';
import { AtlassianIntelligenceBadge } from './AtlassianIntelligenceBadge';
import { AttachmentsBadge } from './AttachmentsBadge';
import { useCardFrontBadgesBoardFragment } from './CardFrontBadgesBoardFragment.generated';
import { useCardFrontBadgesCardFragment } from './CardFrontBadgesCardFragment.generated';
import { ChecklistsBadge } from './ChecklistsBadge';
import { CommentsBadge } from './CommentsBadge';
import { CustomFieldBadges } from './CustomFieldBadges';
import { DescriptionBadge } from './DescriptionBadge';
import { DueDateBadgeWrapper } from './DueDateBadge';
import { ExternalSourceBadge } from './ExternalSourceBadge';
import { LocationBadge } from './LocationBadge';
import { MaliciousAttachmentsBadge } from './MaliciousAttachmentsBadge';
import { NotificationsBadge } from './NotificationsBadge';
import { PluginBadges } from './PluginBadges';
import { SubscribedBadge } from './SubscribedBadge';
import { TemplateBadge } from './TemplateBadge';
import { TrelloAttachmentsBadge } from './TrelloAttachmentsBadge';
import { VotesBadge } from './VotesBadge';

import styles from './CardFrontBadges.less';

export const CardFrontBadges: FunctionComponent = () => {
  const cardId = useCardId();
  const boardId = useBoardId();

  const { data: cardData } = useCardFrontBadgesCardFragment({
    from: { id: cardId },
    optimistic: true,
  });

  const { data: boardData } = useCardFrontBadgesBoardFragment({
    from: { id: boardId },
    optimistic: true,
  });

  const isInbox = useIsInboxBoard();

  const { badges, closed, isTemplate } = cardData ?? {};
  const {
    attachments,
    attachmentsByType,
    checkItems,
    checkItemsChecked,
    checkItemsEarliestDue,
    comments,
    description,
    externalSource,
    lastUpdatedByAi,
    location,
    maliciousAttachments,
    subscribed,
    votes,
    viewingMemberVoted,
    due,
    start,
    dueComplete,
  } = badges || {};

  const { hideVotes, isTemplate: boardIsTemplate } = boardData?.prefs || {};

  const { value: isPersonalProductivityEnabled } = useFeatureGate(
    'trello_personal_productivity_release',
  );

  const [trelloAttachments, nonTrelloAttachments] = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    let trelloAttachments = 0;
    if (attachmentsByType) {
      const { card, board } = attachmentsByType.trello;
      trelloAttachments = (card ?? 0) + (board ?? 0);
    }
    return [
      trelloAttachments,
      attachments ? Math.max(attachments - trelloAttachments, 0) : 0,
    ];
  }, [attachments, attachmentsByType]);

  const unreadNotifications = useSharedStateSelector(
    notificationsState,
    useCallback(({ unreadCount }) => unreadCount[`Card:${cardId}`], [cardId]),
  );

  return (
    <div
      className={styles.badges}
      data-testid={getTestId<CardFrontTestIds>('card-front-badges')}
    >
      <TemplateBadge isTemplate={isTemplate} />
      <NotificationsBadge unreadNotifications={unreadNotifications} />
      {isPersonalProductivityEnabled && isInbox && (
        <ExternalSourceBadge externalSource={externalSource} />
      )}
      <AtlassianIntelligenceBadge
        lastUpdatedByAi={lastUpdatedByAi}
        externalSource={externalSource}
      />
      <SubscribedBadge subscribed={subscribed} />
      <VotesBadge
        votes={votes}
        isTemplate={isTemplate}
        viewingMemberVoted={viewingMemberVoted}
        hideVotes={hideVotes}
      />
      <DueDateBadgeWrapper
        due={due}
        start={start}
        dueComplete={dueComplete}
        isTemplate={isTemplate}
      />
      <DescriptionBadge description={description} />
      <CommentsBadge comments={comments} isBoardTemplate={boardIsTemplate} />
      <AttachmentsBadge attachments={nonTrelloAttachments} />
      <TrelloAttachmentsBadge attachments={trelloAttachments} />
      <MaliciousAttachmentsBadge maliciousAttachments={maliciousAttachments} />
      <ChecklistsBadge
        total={checkItems}
        completed={checkItemsChecked}
        due={checkItemsEarliestDue}
      />
      <ArchivedBadge closed={closed} />
      <LocationBadge location={location} />
      <CustomFieldBadges />
      <PluginBadges />
    </div>
  );
};
